import { Compass, Code2, PenTool, LifeBuoy } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

type Role = {
  label: string;
  title: string;
  desc: string;
  Icon: typeof Compass;
  color: string;
};

const ROLES: ReadonlyArray<Role> = [
  {
    label: 'Fondation',
    title: 'Produit & vision',
    desc: "Vision long terme, architecture technique, choix de conception. Ce qui entre dans Velko, ce qu'on refuse d'y mettre.",
    Icon: Compass,
    color: 'bg-lime-500 text-charcoal-800',
  },
  {
    label: 'Ingénierie',
    title: 'Code qui tient',
    desc: 'Backend Laravel, desktop Tauri/Rust, mobile Flutter. Trois plateformes, un seul comportement. Du code testé, pas du démo-ware.',
    Icon: Code2,
    color: 'bg-terracotta-500 text-white',
  },
  {
    label: 'Design & terrain',
    title: 'Écrans & boutiques',
    desc: "Chaque écran est dessiné puis testé dans une vraie caisse avant d'arriver dans la tienne. Les retours terrain priment sur les tendances.",
    Icon: PenTool,
    color: 'bg-charcoal-700 text-lime-400',
  },
  {
    label: 'Support',
    title: 'Au bout du fil',
    desc: "Une personne qui parle français, comprend ton métier, et peut t'aider à l'installation comme en cas de souci. 7j/7, heures africaines.",
    Icon: LifeBuoy,
    color: 'bg-lime-400 text-charcoal-800',
  },
];

export default function TeamGridV2() {
  return (
    <section className="bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <SectionLabel className="justify-center">L'équipe</SectionLabel>
          <h2 className="mt-6 font-marketing-display text-[clamp(36px,5vw,64px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
            Petite équipe. <span className="underline-lime">Haute exigence.</span>
          </h2>
          <p className="mt-6 font-marketing-body text-lg text-charcoal-600">
            Une équipe resserrée qui code, écoute et livre. Chaque rôle ci-dessous est tenu par des
            personnes qui ont déjà vécu dans le commerce en Afrique francophone.
          </p>
        </div>

        <ul className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {ROLES.map((r, idx) => (
            <li key={r.title}>
              <article
                className={`flex h-full flex-col border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-6 transition-transform hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-10 ${idx % 2 === 1 ? 'md:translate-y-3' : ''}`}
              >
                <div
                  className={`flex h-14 w-14 items-center justify-center border-2 border-charcoal-800 shadow-neobrutal-2 ${r.color}`}
                >
                  <r.Icon className="h-6 w-6" strokeWidth={2.2} />
                </div>
                <div className="mt-5 font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-600">
                  {r.label}
                </div>
                <div className="mt-1 font-marketing-display text-[20px] font-bold leading-tight text-charcoal-800">
                  {r.title}
                </div>
                <p className="mt-4 font-marketing-body text-[14.5px] leading-relaxed text-charcoal-700">
                  {r.desc}
                </p>
              </article>
            </li>
          ))}
        </ul>

        <div className="mt-16 border-2 border-charcoal-800 bg-white p-6 shadow-neobrutal-6 md:p-8">
          <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
            <p className="max-w-3xl font-marketing-body text-[15.5px] leading-relaxed text-charcoal-700 md:text-base">
              <b className="text-charcoal-900">Engagement long terme.</b> Pas de levée opportuniste,
              pas d'exit planifié à cinq ans. On construit Velko pour tenir — parce qu'une caisse,
              ça se change pas tous les matins.
            </p>
            <NeobrutalButton as="link" to="/contact" variant="secondary" size="md">
              Nous rejoindre
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M5 12h14" />
                <path d="M13 6l6 6-6 6" />
              </svg>
            </NeobrutalButton>
          </div>
        </div>
      </div>
    </section>
  );
}
